import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeChargeType } from '../../models/RoomBooking';
import { getStripe, getStripeWithAccount } from '../../services/stripeService';

interface PayStepProps {
  clientSecret: string;
  stripeChargeType?: StripeChargeType;
  stripeAccountId?: string;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}

function PaymentForm({ 
  onPaymentSuccess, 
  onPaymentError, 
  isProcessing, 
  setIsProcessing,
  stripeChargeType,
  stripeAccountId
}: { 
  onPaymentSuccess: () => void; 
  onPaymentError: (error: string) => void; 
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  stripeChargeType?: StripeChargeType;
  stripeAccountId?: string;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      // Provide better error messages for common issues
      let errorMessage = submitError.message || 'Payment failed';
      
      if (submitError.type === 'validation_error') {
        if (submitError.message?.includes('card')) {
          errorMessage = t('payment.error.selectPaymentMethod', 'Please select a payment method to continue');
        } else if (submitError.message?.includes('incomplete')) {
          errorMessage = t('payment.error.incompleteForm', 'Please complete all payment details');
        } else {
          errorMessage = t('payment.error.validation', 'Please check your payment information and try again');
        }
      } else if (submitError.type === 'card_error') {
        errorMessage = t('payment.error.card', 'There was an issue with your card. Please try again');
      } else if (submitError.type === 'invalid_request_error') {
        errorMessage = t('payment.error.invalidRequest', 'Invalid payment request. Please try again');
      }
      
      setError(errorMessage);
      onPaymentError(errorMessage);
      setIsProcessing(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
      redirect: "if_required",
    });

    if (confirmError) {
      let errorMessage = confirmError.message || 'Payment failed';
      
      if (confirmError.type === 'card_error') {
        errorMessage = t('payment.error.cardDeclined', 'Your card was declined. Please try a different payment method');
      } else if (confirmError.type === 'validation_error') {
        errorMessage = t('payment.error.validation', 'Please check your payment information and try again');
      }
      
      setError(errorMessage);
      onPaymentError(errorMessage);
    } else {
      onPaymentSuccess();
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} id="payment-form">
      <Stack spacing={3}>
        <PaymentElement 
          onChange={() => {
            // Clear error when user interacts with payment element
            if (error) {
              setError(null);
            }
          }}
          onReady={() => {
            // Clear error when payment element is ready
            if (error) {
              setError(null);
            }
          }}
        />
        
        {error && (
          <Alert 
            severity="error"
            onClose={() => setError(null)}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => setError(null)}
              >
                {t('common.dismiss', 'Dismiss')}
              </Button>
            }
          >
            {error}
          </Alert>
        )}
      </Stack>
    </form>
  );
}

export default function PayStep({ 
  clientSecret, 
  stripeChargeType, 
  stripeAccountId, 
  onPaymentSuccess, 
  onPaymentError, 
  isProcessing, 
  setIsProcessing 
}: PayStepProps) {
  const { t } = useTranslation();

  if (!clientSecret) {
    return (
      <Box sx={{ py: 3, width: '100%', maxWidth: 800, mx: 'auto' }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Alert severity="error">
            {t('payment.noClientSecret', 'Payment information not available')}
          </Alert>
        </Paper>
      </Box>
    );
  }

  // Get the appropriate Stripe instance based on charge type and account ID
  const getStripeInstance = () => {
    if (stripeChargeType === StripeChargeType.DIRECT && stripeAccountId) {
      return getStripeWithAccount(stripeAccountId);
    }
    return getStripe();
  };

  return (
    <Box sx={{ py: 3, width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {t('payment.completePayment', 'Complete Payment')}
          </Typography>

          <Elements 
            stripe={getStripeInstance()} 
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#1976d2',
                },
              },
            }}
          >
            <PaymentForm 
              onPaymentSuccess={onPaymentSuccess}
              onPaymentError={onPaymentError}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
              stripeChargeType={stripeChargeType}
              stripeAccountId={stripeAccountId}
            />
          </Elements>
        </Stack>
      </Paper>
    </Box>
  );
} 